import { AlertDangerIcon, AlertWarningIcon, EllipsisText, Feedback, Stack } from 'canoe';
import { Form, useFormikContext } from 'formik';
import React, { useEffect } from 'react';

import { ContentPlacement } from '@candu-ai/placement/types';
import {
  CompletedStepSettings,
  EditLinkButton,
  InlineMessage,
  StepSectionTitle,
} from 'craft/containers/Toolbox/tabs/Placements/components/ui';
import { FormState } from 'craft/containers/Toolbox/tabs/Placements/constants';
import { checkInvalidSelector } from 'craft/containers/Toolbox/tabs/Placements/utils';
import { useCurrentSnapshot, useToolboxTab } from 'craft/state';

import { InlineHTMLSelectorForm } from './InlineHTMLSelectorForm';

export interface SelectorProps {
  active: boolean;
  state: FormState;
  handleRetargetClick: () => void;
  onEdit: VoidFunction;
  onSave: (value?: string) => void;
  onCancel: VoidFunction;
  onDeletePlacementSnapshot: VoidFunction;
}

export const InlineSelector = ({
  active,
  state,
  onEdit,
  handleRetargetClick,
  onSave,
  onCancel,
  onDeletePlacementSnapshot,
}: SelectorProps) => {
  const { snapshot } = useCurrentSnapshot();
  const { forcePlacementsTab } = useToolboxTab();
  const { values } = useFormikContext<ContentPlacement>();

  const { invalidSelector, querySelector } = checkInvalidSelector(values.selector);
  const notVisibleOnPage =
    !invalidSelector && !!values.snapshotId && !querySelector && !!values.selector;

  const shouldForceEdit =
    invalidSelector || (!values.selector && state === FormState.UPDATE) || notVisibleOnPage;

  useEffect(() => {
    // if we have an invalid selector or selector is not visible on page, force active
    if (!active && shouldForceEdit) {
      onEdit();
    }
  }, [active, shouldForceEdit]);

  if (active) {
    return (
      <Form data-cy="form-placement">
        <StepSectionTitle size="short" active>
          HTML Selector?
        </StepSectionTitle>
        <InlineHTMLSelectorForm
          handleRetargetClick={snapshot ? handleRetargetClick : undefined}
          onSave={onSave}
          onCancel={onCancel}
          isForcedEdit={shouldForceEdit}
          onDeletePlacementSnapshot={onDeletePlacementSnapshot}
        />
      </Form>
    );
  }

  return (
    <>
      <CompletedStepSettings
        hasError={invalidSelector || (!values.selector && state === FormState.UPDATE)}
        hasWarning={notVisibleOnPage}
      >
        <Stack space="xs" css={{ overflow: 'hidden' }}>
          <span>
            <strong>HTML Selector</strong>
          </span>
          <span>
            <EllipsisText content={values.selector}>
              <div>{values.selector}</div>
            </EllipsisText>
          </span>
        </Stack>
        <EditLinkButton
          appearance="text"
          inline
          size="small"
          variant="white"
          fontWeight="xbold"
          onClick={onEdit}
          disabled={forcePlacementsTab && !!values.id}
        >
          edit
        </EditLinkButton>
      </CompletedStepSettings>
      {!values.selector && state === FormState.UPDATE && (
        <InlineMessage>
          <Feedback ml="xxs" color="danger" mode="alert" alertIcon={AlertDangerIcon}>
            Required
          </Feedback>
        </InlineMessage>
      )}
      {invalidSelector && (
        <InlineMessage>
          <Feedback ml="xxs" color="danger" mode="alert" alertIcon={AlertDangerIcon}>
            Selector is not valid
          </Feedback>
        </InlineMessage>
      )}
      {notVisibleOnPage && (
        <InlineMessage>
          <Feedback ml="xxs" color="warning" mode="alert" alertIcon={AlertWarningIcon}>
            Selector not visible on page
          </Feedback>
        </InlineMessage>
      )}
    </>
  );
};
